import { nullishFilter } from '@caresend/utils';
import { createPopper } from '@popperjs/core';
import { defineComponent, computed, ref, watch } from 'vue';
import VueSelect from 'vue-select';
import __vue_component__$2 from '../../../assets/SmallX.svg.vue.js';
import TransitionExpand from '../../transitions/TransitionExpand/TransitionExpand.js';
import __vue_component__$1 from '../../ui/Skeleton.vue.js';
import __vue_component__ from '../../ui/Tooltip/Tooltip.vue.js';
import __vue_component__$3 from '../../ui/UnsubscribedTag.vue.js';
import { reportException } from '../../../functions/sentry.js';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import '../../ui/Icon.vue.js';
import '../../../functions/configuration/icons.js';
import { debounce } from '../../../functions/debounce.js';
import '../../../database/algoliaConfig/indices.js';
import '@caresend/types';
import 'mobile-device-detect';
import '../../../functions/tracking/index.js';

var script = defineComponent({
    name: 'RichSelect',
    components: {
        Tooltip: __vue_component__,
        TransitionExpand,
        Skeleton: __vue_component__$1,
        SmallX: __vue_component__$2,
        VueSelect,
        UnsubscribedTag: __vue_component__$3,
    },
    props: {
        /**
         * Pass a getter that returns a string to show as a second line on each
         * option. The returned string will be searchable by typing in the input.
         */
        additionalInfoGetter: {
            type: Function,
            default: null,
        },
        /**
         * Whether to show an option to add a new entry at the top of the list.
         */
        allowAddNew: Boolean,
        /**
         * Whether the dropdown list should grow to fit the width of its options,
         * if wider than the input box. (Normally the dropdown list is the same
         * width as the input box.)
         */
        allowWiderDropdown: Boolean,
        /**
         * @deprecated
         * Remove when prop usages are removed.
         */
        appendToBody: {
            type: Boolean,
            default: null,
        },
        /**
         * Support all options regardless of filter.
         * Ex: LocationInput
         */
        bypassFilter: Boolean,
        debounceSearch: Boolean,
        /**
         * If provided, the search will immediately happen with this value
         */
        defaultSearch: {
            type: String,
            default: null,
        },
        /**
         * Pass `true` to disable the input. Pass a string to disable the input
         * and show a tooltip on hover.
         */
        disabled: {
            type: [Boolean, String],
            default: false,
        },
        /** Display the option ID underneath each option */
        displayID: Boolean,
        /**
         * A name used to describe what the select is used for. For example, if
         * entityName is 'patient', messages such as 'No patient found for
         * "Florian", and 'Add a new patient' will show.
         */
        entityName: {
            type: String,
            default: null,
        },
        iconLeft: {
            type: String,
            default: null,
        },
        /** Label text to show above input */
        label: {
            type: String,
            default: null,
        },
        /** The entire input is loading and not yet usable. */
        loading: Boolean,
        /** Whether to allow multiple selections */
        multiple: Boolean,
        /** An array of objects representing the available options. */
        options: {
            type: Array,
            required: true,
        },
        /** Placeholder text to show in the select when it is empty */
        placeholder: {
            type: String,
            default: '',
        },
        /**
         * An async search is loading. No results message will be replaced with
         * 'Loading…'
         */
        searchLoading: Boolean,
        /** The size of the input */
        size: {
            type: String,
            default: 'md',
        },
        smallLabel: Boolean,
        /**
         * If multiple is true, the value passed must be an array.
         * If multiple is false, the value passed must be an object.
         */
        value: {
            type: [Array, Object],
            default: null,
        },
        /**
         * Whether to show the unsubscribed tag next to the selected option
         *
         * IMPORTANT: supports single selection only
         */
        showUnsubscribedTag: Boolean,
    },
    setup(props, { emit }) {
        const classNames = computed(() => {
            const names = [];
            switch (props.size) {
                case 'sm':
                    names.push('RichSelect--sm');
                    break;
                case 'md':
                    names.push('RichSelect--md');
            }
            if (props.iconLeft)
                names.push('RichSelect--with-icon-left');
            if (props.smallLabel)
                names.push('RichSelect--small-label');
            if (props.value)
                names.push('RichSelect--with-selection');
            return names;
        });
        const optionClassNames = computed(() => {
            const names = [];
            if (props.iconLeft)
                names.push('RichSelect__option--with-icon-left');
            return names;
        });
        const selectedOptions = ref(
        /**
         * The prop multiple is true, so `value` will be an array and it is safe
         * to typecast.
         */
        !props.multiple ? undefined
            : props.value);
        watch(() => props.value, () => {
            if (!props.multiple)
                return;
            selectedOptions.value = props.value;
        });
        const vueSelectVm = ref(null);
        const addNewLabel = computed(() => props.entityName ? `Add a new ${props.entityName}` : 'Add new');
        const addNewIsHighlighted = ref(false);
        const addNewClassNames = computed(() => addNewIsHighlighted.value ? ['RichSelect__add-new--highlighted'] : []);
        const setAddNewIsHighlighted = (newIsHighlighted) => {
            addNewIsHighlighted.value = newIsHighlighted;
            if (newIsHighlighted && vueSelectVm.value) {
                vueSelectVm.value.typeAheadPointer = -1;
            }
        };
        const addNew = () => {
            var _a, _b;
            emit('input', null);
            emit('addNew');
            if (vueSelectVm.value) {
                vueSelectVm.value.open = false;
                // Blurring vue select will clear any search and prepare it for reuse.
                (_b = (_a = document.activeElement) === null || _a === void 0 ? void 0 : _a.blur) === null || _b === void 0 ? void 0 : _b.call(_a);
            }
        };
        const keyDownHandlers = (map, vm) => {
            if (!props.allowAddNew)
                return map;
            return {
                ...map,
                38(event) {
                    var _a;
                    if (vm.typeAheadPointer === 0) { // First option highlighted
                        setAddNewIsHighlighted(true);
                    }
                    else {
                        if (addNewIsHighlighted.value)
                            setAddNewIsHighlighted(false);
                        (_a = map[38]) === null || _a === void 0 ? void 0 : _a.call(map, event);
                    }
                },
                40(event) {
                    var _a, _b;
                    if (vm.typeAheadPointer === -1) { // No option highlighted
                        if (addNewIsHighlighted.value) {
                            setAddNewIsHighlighted(false);
                            (_a = map[40]) === null || _a === void 0 ? void 0 : _a.call(map, event);
                        }
                        else {
                            setAddNewIsHighlighted(true);
                        }
                    }
                    else {
                        (_b = map[40]) === null || _b === void 0 ? void 0 : _b.call(map, event);
                    }
                },
                13(event) {
                    var _a;
                    if (addNewIsHighlighted.value) {
                        addNew();
                    }
                    else {
                        (_a = map[13]) === null || _a === void 0 ? void 0 : _a.call(map, event);
                    }
                },
            };
        };
        const calculatePosition = (dropdownList, component, { width, top, left }) => {
            // Allow the dropdown to grow wider than the input box if prop is set.
            if (props.allowWiderDropdown) {
                dropdownList.style.setProperty('width', 'auto');
                const actualWidth = dropdownList.offsetWidth;
                if (actualWidth < parseInt(width, 10)) {
                    dropdownList.style.setProperty('width', width);
                }
            }
            else {
                dropdownList.style.setProperty('width', width);
            }
            if (!(component.$refs.toggle instanceof Element)) {
                // Fallback to manual placement if element needed for popper is not
                // available.
                dropdownList.style.setProperty('top', top);
                dropdownList.style.setProperty('left', left);
                reportException('Expected toggle to be instance of Element');
                return;
            }
            // Position with popper, relative to the toggle element.
            const popper = createPopper(component.$refs.toggle, dropdownList, {
                placement: 'bottom-start',
                modifiers: [
                    {
                        name: 'flip',
                        options: { fallbackPlacements: [] },
                    },
                    {
                        name: 'offset',
                        options: { offset: [0, -1] },
                    },
                ],
            });
            return () => popper.destroy();
        };
        watch(() => { var _a; return (_a = vueSelectVm.value) === null || _a === void 0 ? void 0 : _a.typeAheadPointer; }, (newPointer) => {
            if (newPointer !== -1)
                setAddNewIsHighlighted(false);
        });
        const noMatchMessage = (search) => {
            var _a;
            if (search === '')
                return props.placeholder;
            if (props.searchLoading)
                return 'Loading…';
            const forSearch = search ? ` for “${search}”` : '';
            return `No ${(_a = props.entityName) !== null && _a !== void 0 ? _a : 'matches'} found${forSearch}`;
        };
        /**
         * @type {import('vue-select').VueSelectProps['components']}
         * Type above included for reference. Using the type directly will result
         * in TS2742 error as Vue Select imports Vue in its types.
         */
        const components = {
            Deselect: {
                render: (createElement) => createElement('span', '✕'),
            },
            // Do not render open indicator (chevron)
            OpenIndicator: {
                render: (createElement) => createElement('span', ''),
            },
        };
        // Ensure the "add new" option always shows if allowAddNew is enabled.
        const filterBy = (option, label, search) => {
            const labelMatches = (label || '').toLowerCase().indexOf(search.toLowerCase()) > -1;
            // Allow ID match if displayID is enabled
            const idMatches = props.displayID && (option.id || '').toLowerCase().indexOf(search.toLowerCase()) > -1;
            // Allow additional info match if additionalInfoGetter is enabled
            const additionalInfoMatches = props.additionalInfoGetter
                && props.additionalInfoGetter(option).toLowerCase().indexOf(search.toLowerCase()) > -1;
            return props.bypassFilter || labelMatches || idMatches || additionalInfoMatches;
        };
        /**
         * @deprecated
         * Remove when prop usages are removed.
         */
        const appendDropdownToBody = computed(() => true);
        const optionSelected = (option) => {
            emit('input', option);
        };
        /**
         * Emit the new selected options array which will not contain the
         * de-selected option
         */
        const optionDeselected = (option) => {
            var _a;
            if (props.disabled)
                return;
            const newSelection = (_a = selectedOptions.value) === null || _a === void 0 ? void 0 : _a.filter((o) => o.id !== option.id);
            emit('input', newSelection);
        };
        /** This is for the single selection when the user clicks on the 'cross'
         * symbol to delete the current selected value, if they clicked on this
         * button, then the 'option' variable should be null
         *
         * If it is, emit null
         *
         * We handle all other cases of input via optionSelected and optionDeselected
         */
        const input = (option) => {
            if (option === null)
                emit('input', null);
        };
        const selectedOptionsMeta = computed(() => {
            var _a;
            return Object.fromEntries(([
                ...(_a = selectedOptions.value) !== null && _a !== void 0 ? _a : [],
                ...(props.value instanceof Array ? props.value : [props.value]),
            ])
                .filter(nullishFilter)
                .map((o) => [o.id, o]));
        });
        const getIsHovered = (id) => { var _a, _b; return (_b = (_a = selectedOptionsMeta.value[id]) === null || _a === void 0 ? void 0 : _a.isHovered) !== null && _b !== void 0 ? _b : false; };
        const setIsHovered = (id, value) => {
            var _a;
            if (!selectedOptions.value)
                return;
            const selectedOptionIndex = selectedOptions.value.findIndex((option) => option.id === id);
            const selectedOption = selectedOptions.value[selectedOptionIndex];
            if (!selectedOption)
                return;
            const isHovered = (_a = value !== null && value !== void 0 ? value : !selectedOption.isHovered) !== null && _a !== void 0 ? _a : false;
            selectedOptions.value.splice(selectedOptionIndex, 1, {
                ...selectedOption,
                isHovered,
            });
        };
        const getOptionTitleClasses = (id) => {
            const classes = [];
            if (selectedOptionsMeta.value[id])
                classes.push('RichSelect__option-title--selected');
            return classes;
        };
        const getSelectedOptionColorStyles = (currentOption) => {
            const option = {};
            const closeIcon = {};
            if (currentOption.color) {
                option.border = `1px solid ${currentOption.color}`;
                option.background = `${currentOption.color}1A`;
                closeIcon.fill = currentOption.color;
            }
            if (props.disabled) {
                option.cursor = 'auto';
            }
            return {
                closeIcon,
                option,
            };
        };
        const onSearch = computed(() => {
            if (props.debounceSearch) {
                return debounce((search) => emit('search', search), 200);
            }
            return (search) => emit('search', search);
        });
        watch(() => props.defaultSearch, (search) => {
            if (search)
                emit('search', search);
        }, { immediate: true });
        return {
            addNew,
            addNewClassNames,
            addNewLabel,
            appendDropdownToBody,
            calculatePosition,
            classNames,
            components,
            filterBy,
            getIsHovered,
            getOptionTitleClasses,
            getSelectedOptionColorStyles,
            input,
            keyDownHandlers,
            noMatchMessage,
            onSearch,
            optionClassNames,
            optionDeselected,
            optionSelected,
            selectedOptions,
            selectedOptionsMeta,
            setAddNewIsHighlighted,
            setIsHovered,
            vueSelectVm,
        };
    },
});

export { script as default };
